import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import WidgetsRoundedIcon from '@mui/icons-material/WidgetsRounded';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { Link } from 'react-router-dom';
import { selectedTemplateRouteRef } from '../../routes';
import { useRouteRef } from '@backstage/core-plugin-api';
import { CATEGORY_ICONS } from '../icons';

type Props = {
  template: TemplateEntityV1beta3;
};

export const TemplateCard = ({ template }: Props) => {
  const theme = useTheme();
  const templateRoute = useRouteRef(selectedTemplateRouteRef);

  const {
    metadata: { name, title, description, tags },
    spec: { type, owner },
  } = template;

  const namespace = template.metadata.namespace || 'default';
  const templatePath = templateRoute({
    namespace,
    templateName: name,
  });

  // Find matching category icon from tags
  const categoryTag = tags?.find(tag => CATEGORY_ICONS[tag]);
  const categoryIcon = categoryTag ? (
    CATEGORY_ICONS[categoryTag]
  ) : (
    <WidgetsRoundedIcon sx={{ fontSize: 20 }} />
  );

  const displayTags = [
    type ? `Type: ${type}` : '',
    owner ? `Owner: ${owner}` : '',
    ...(tags?.filter(tag => !CATEGORY_ICONS[tag]).slice(0, 2) || []),
  ].filter(Boolean);

  const truncateText = (text?: string, maxLength: number = 150) => {
    if (!text) return 'No description available';
    if (text.length <= maxLength) return text;
    return `${text.substring(0, maxLength)}...`;
  };

  return (
    <Card
      component={Link}
      to={templatePath}
      elevation={0}
      sx={{
        borderRadius: 6,
        border: `1px solid ${
          theme.palette.mode === 'dark'
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(15,20,30,0.08)'
        }`,
        backgroundImage: 'none',
        p: 2.5,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color .2s ease, transform .2s ease',
        '&:hover': {
          borderColor: 'info.main',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', mb: 1.5 }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.info.main, 0.15),
            color: 'info.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { fontSize: 20 },
          }}
        >
          {categoryIcon}
        </Box>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 15,
            lineHeight: 1.3,
            flex: 1,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {title || name}
        </Typography>
      </Stack>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          mb: 1.5,
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 3,
          overflow: 'hidden',
        }}
      >
        {truncateText(description)}
      </Typography>

      {displayTags.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{ mb: 1.5, flexWrap: 'wrap' }}
        >
          {displayTags.map((tag, idx) => (
            <Chip key={`${tag}-${idx}`} label={tag} size="small" />
          ))}
        </Stack>
      )}

      <Box sx={{ flex: 1 }} />

      <Stack
        direction="row"
        sx={{
          color: 'text.secondary',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
      </Stack>
    </Card>
  );
};
